'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

// Each option carries a risk weight (higher = more risk) and a gap flag that
// surfaces a specific recommendation when chosen.
interface Option {
  text: string;
  risk: number; // 0 (good) .. 3 (worst)
  gap?: string; // recommendation triggered when this option is selected
}

interface Question {
  id: string;
  text: string;
  helpText?: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    id: 'sensitive_data',
    text: 'Do you handle regulated or high-value data?',
    helpText: 'PII, PHI, cardholder data, financials, source code, and IP all raise the stakes of a leak.',
    options: [
      { text: 'No sensitive data of any consequence', risk: 0 },
      { text: 'Some PII, but nothing heavily regulated', risk: 1, gap: 'Even basic PII triggers breach-notification duties. Discover and classify what you hold so you can protect it.' },
      { text: 'Yes—PHI, cardholder data, or financials', risk: 3, gap: 'You hold regulated data, so a single leak is a reportable event. Discovery, classification, and DLP enforcement should be in place.' },
      { text: 'Yes—plus source code, IP, or trade secrets', risk: 3, gap: 'Regulated data and crown-jewel IP both leave through everyday channels. Full-channel DLP is warranted here.' },
    ],
  },
  {
    id: 'genai',
    text: 'Do employees use GenAI tools (ChatGPT, Copilot) for work?',
    helpText: 'Pasting confidential data into AI tools ("shadow AI") is the fastest-growing leak path.',
    options: [
      { text: 'Yes, with DLP/guardrails on what they can paste', risk: 0 },
      { text: 'Yes, with a written policy but no technical controls', risk: 2, gap: 'A policy without enforcement relies on memory. Add DLP that detects sensitive content heading into AI prompts and uploads.' },
      { text: 'Yes, freely—no policy or controls', risk: 3, gap: 'Unguarded GenAI use is an open exfiltration channel. Deploy browser/endpoint DLP to govern what can be pasted into AI tools.' },
      { text: 'We think so, but have no visibility', risk: 3, gap: 'Shadow AI you can\'t see is the worst case. Discovery and DLP will surface and control unsanctioned AI use.' },
    ],
  },
  {
    id: 'email_controls',
    text: 'How is sensitive data controlled over email?',
    helpText: 'Misdirected email and unencrypted attachments are the classic accidental-leak channel.',
    options: [
      { text: 'DLP scans outbound email; sensitive data is auto-encrypted or blocked', risk: 0 },
      { text: 'Encryption is available but manual / opt-in', risk: 2, gap: 'Manual encryption gets skipped under deadline. Automate it with DLP policies that act on content, not user memory.' },
      { text: 'No outbound controls—we trust people to be careful', risk: 3, gap: 'No outbound email DLP means one wrong recipient is a breach. Add content-aware scanning that warns, encrypts, or blocks.' },
      { text: 'Not sure what\'s in place', risk: 2, gap: 'Unknown email controls. An assessment will show whether sensitive data can leave over email unchecked.' },
    ],
  },
  {
    id: 'endpoint_cloud',
    text: 'Are USB, cloud-share, and SaaS data movements controlled?',
    helpText: 'USB copies, "anyone with the link" shares, and shadow-IT SaaS are common offline/cloud leak paths.',
    options: [
      { text: 'Yes—endpoint DLP plus CASB cover USB, cloud, and SaaS', risk: 0 },
      { text: 'Some controls, but gaps across USB or SaaS', risk: 2, gap: 'Partial coverage leaves open paths. Extend DLP across endpoints, cloud storage, and SaaS so policy follows the data.' },
      { text: 'No—USB and cloud sharing are wide open', risk: 3, gap: 'Uncontrolled USB and cloud sharing are top exfiltration routes. Endpoint DLP and a CASB close them.' },
      { text: 'Not sure', risk: 2, gap: 'No visibility into endpoint/cloud movement. Discovery will reveal where data is leaving and what to lock down.' },
    ],
  },
  {
    id: 'near_miss',
    text: 'Have you had a near-miss or actual data-leak incident?',
    helpText: 'A misdirected file or exposed share is a strong signal the everyday channels need controls.',
    options: [
      { text: 'No incidents, and we\'d detect one if it happened', risk: 0 },
      { text: 'A near-miss or two we caught informally', risk: 2, gap: 'Near-misses you caught by luck will eventually slip through. Put detective and preventive DLP controls in place.' },
      { text: 'Yes—we\'ve had data leave that shouldn\'t have', risk: 3, gap: 'A real leak already happened. Prioritize discovery, classification, and enforced DLP to stop the next one.' },
      { text: 'We wouldn\'t know if data leaked', risk: 3, gap: 'No detection means leaks go unnoticed. Monitoring and alerting are the first DLP capabilities to stand up.' },
    ],
  },
  {
    id: 'compliance',
    text: 'Which compliance regime applies to you?',
    helpText: 'HIPAA, PCI-DSS, SOC 2, and GDPR all expect data-protection controls like DLP.',
    options: [
      { text: 'None that mandate data-protection controls', risk: 0 },
      { text: 'One regime, and our controls already map to it', risk: 1, gap: 'Keep DLP evidence audit-ready as your data and scope grow—controls drift over time.' },
      { text: 'One or more (HIPAA, PCI, SOC 2, GDPR) with gaps', risk: 3, gap: 'Your regime expects data-protection controls you don\'t fully have. DLP provides the enforcement and audit-ready evidence.' },
      { text: 'Not sure which applies to us', risk: 2, gap: 'Unclear compliance scope is its own risk. Map your obligations, then align DLP controls to them.' },
    ],
  },
];

type Status = 'intro' | 'assessment' | 'results';

interface Answer {
  questionId: string;
  optionIndex: number;
}

function levelFor(percent: number): {
  level: 'High' | 'Medium' | 'Low';
  color: string;
  bg: string;
  blurb: string;
} {
  if (percent >= 55) {
    return {
      level: 'High',
      color: 'text-red-700 dark:text-red-300',
      bg: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
      blurb:
        'Your sensitive data can leave through channels you don\'t control—the everyday email, cloud, USB, and GenAI paths attackers and accidents exploit. Discovering and classifying your data, then standing up DLP enforcement, should be an immediate priority.',
    };
  }
  if (percent >= 25) {
    return {
      level: 'Medium',
      color: 'text-amber-700 dark:text-amber-300',
      bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
      blurb:
        'You have some controls but meaningful gaps remain—often around GenAI use, USB/cloud sharing, or audit-ready evidence. Closing them will materially lower your data-leak risk.',
    };
  }
  return {
    level: 'Low',
    color: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800',
    blurb:
      'Your data-protection posture is strong. A managed DLP service keeps it that way—classifying new data, tuning policies, and covering new channels like GenAI as you grow.',
  };
}

export default function RiskCheckClient() {
  const [status, setStatus] = useState<Status>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [userCount, setUserCount] = useState<number>(25);

  const handleAnswer = (optionIndex: number) => {
    const question = QUESTIONS[currentIndex];
    const newAnswer: Answer = { questionId: question.id, optionIndex };
    const newAnswers = [...answers.filter((a) => a.questionId !== question.id), newAnswer];
    setAnswers(newAnswers);

    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStatus('results');
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRestart = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setStatus('intro');
  };

  const result = useMemo(() => {
    if (answers.length !== QUESTIONS.length) return null;
    let total = 0;
    const maxPerQuestion = 3;
    const gaps: string[] = [];
    QUESTIONS.forEach((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      if (!a) return;
      const option = q.options[a.optionIndex];
      total += option.risk;
      if (option.gap) gaps.push(option.gap);
    });
    const max = QUESTIONS.length * maxPerQuestion;
    const percent = Math.round((total / max) * 100);
    return { percent, gaps, ...levelFor(percent) };
  }, [answers]);

  const resultsSummary = useMemo(() => {
    if (!result) return '';
    const answerLines = QUESTIONS.map((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      const chosen = a ? q.options[a.optionIndex].text : 'N/A';
      return `  • ${q.text} → ${chosen}`;
    }).join('\n');

    const gapLines = result.gaps.length
      ? result.gaps.map((g, i) => `${i + 1}. ${g}`).join('\n')
      : 'No major gaps identified—maintain current controls.';

    return `DATA LOSS PREVENTION RISK CHECK RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Data-Leak Risk: ${result.level} (${result.percent}% risk score)
Approx. users / accounts: ${userCount}

${result.blurb}

ANSWERS:
${answerLines}

TOP GAPS TO CLOSE:
${gapLines}`;
  }, [result, answers, userCount]);

  // Intro
  if (status === 'intro') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🛡️</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">How exposed is your sensitive data?</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
              Six questions on the data you hold, GenAI use, email/USB/cloud controls, past near-misses, and your compliance regime. Get an instant Data-Leak Risk score and your top gaps.
            </p>
          </div>

          <div className="bg-[#2813e8]/5 border border-[#2813e8]/20 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-[#2813e8] mb-2">What you&apos;ll learn:</h3>
            <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-1">
              <li>• Whether your data-leak risk is High, Medium, or Low</li>
              <li>• The specific channels leaving you exposed</li>
              <li>• What to fix first to close the biggest holes</li>
            </ul>
          </div>

          <button
            onClick={() => setStatus('assessment')}
            className="w-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition-opacity"
          >
            Start the Risk Check
          </button>
        </div>
      </div>
    );
  }

  // Assessment
  if (status === 'assessment') {
    const question = QUESTIONS[currentIndex];
    const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
    const currentAnswer = answers.find((a) => a.questionId === question.id);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Data-Leak Risk Check</span>
              <span>Question {currentIndex + 1} of {QUESTIONS.length}</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{question.text}</h2>
            {question.helpText && <p className="text-gray-600 dark:text-gray-400 text-sm">{question.helpText}</p>}
          </div>

          <div className="space-y-3 mb-6">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  currentAnswer?.optionIndex === idx
                    ? 'border-[#2813e8] bg-[#2813e8]/5'
                    : 'border-gray-200 dark:border-gray-700 hover:border-[#2813e8]/40 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-gray-900 dark:text-gray-100">{option.text}</span>
              </button>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>
            <button
              onClick={handleRestart}
              className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (status === 'results' && result) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Score */}
        <div className={`rounded-2xl border-2 p-8 ${result.bg}`}>
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">{result.level === 'High' ? '🚨' : result.level === 'Medium' ? '⚠️' : '✅'}</div>
            <h2 className={`text-3xl font-bold mb-2 ${result.color}`}>Data-Leak Risk: {result.level}</h2>
            <p className="text-gray-700 dark:text-gray-300 max-w-xl mx-auto">{result.blurb}</p>
          </div>
          <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Risk score</div>
            <div className={`text-3xl font-bold ${result.color}`}>{result.percent}%</div>
          </div>
        </div>

        {/* Gaps */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            {result.gaps.length ? 'Top Gaps to Close' : 'No Major Gaps Identified'}
          </h3>
          {result.gaps.length ? (
            <ul className="space-y-3">
              {result.gaps.map((gap, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#2813e8]/10 text-xs font-bold text-[#2813e8]">{i + 1}</span>
                  <span className="text-gray-700 dark:text-gray-300 leading-relaxed">{gap}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">Your controls look solid. A managed DLP service keeps classification, policy tuning, and new-channel coverage tight as you scale.</p>
          )}
        </div>

        {/* User count */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            How many users / endpoints would this cover? <span className="text-[#2813e8] font-bold">{userCount}</span>
          </label>
          <input
            type="range"
            min="1"
            max="500"
            step="1"
            value={userCount}
            onChange={(e) => setUserCount(Number(e.target.value))}
            aria-label="Number of users"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>1</span>
            <span>250</span>
            <span>500</span>
          </div>
        </div>

        {/* Offer CTA */}
        <div className="rounded-2xl border-2 border-[#2813e8]/30 bg-[#2813e8]/5 p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Want these gaps closed for you?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Inventive HQ deploys managed DLP that discovers and classifies your sensitive data, then monitors and blocks risky movement across email, endpoints, cloud, and GenAI tools.
          </p>
          <Link
            href="/services/data-loss-prevention"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] px-8 py-3 font-semibold text-white shadow-lg transition-all hover:scale-105"
          >
            See the Managed DLP Service
          </Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleRestart}
            className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            🔄 Retake Check
          </button>
        </div>

        {/* Lead capture */}
        <div className="mt-8">
          <LeadCaptureForm
            source="dlp"
            title="Get Your Data-Leak Risk Reviewed by an Expert"
            description="Our security team will review your results and send a prioritized plan to close your data-leak gaps across email, endpoints, cloud, and AI tools."
            deliverable="a prioritized data-protection plan with per-user pricing"
            resultsSummary={resultsSummary}
          />
        </div>
      </div>
    );
  }

  return null;
}
